import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DataTablesModule } from 'angular-datatables';
import { InlineSVGModule } from 'ng-inline-svg';
import { CookieService } from 'ngx-cookie-service';
import { NgxPaginationModule } from 'ngx-pagination';
import { AngularMaterialModule } from './angular-material.module';
import { AccessPointBannerComponent } from './components/access-point-banner/access-point-banner.component';
import { AutoCloseDialogComponent } from './components/auto-close-dialog/auto-close-dialog.component';
import { CarrierLogoModule } from './components/carrier-logo/carrier-logo.module';
import {
    CircularConfidenceGaugeComponent,
} from './components/circular-confidence-gauge/circular-confidence-gauge.component';
import { ComplaintsComponent } from './components/complaints/complaints.component';
import { ConfidenceGaugeModule } from './components/confidence-gauge/confidence-gauge.module';
import { ContentLoadingProgressModule } from './components/content-loading-progress/content-loading-progress.module';
import { CustomDatePickerComponent } from './components/custom-date-picker/custom-date-picker.component';
import { DataTableModule } from './components/data-table/data-table.module';
import {
    DeliveryDefenseScoreSummaryModule,
} from './components/delivery-defense-score-summary/delivery-defense-score-summary.module';
import {
    DeliveryDefenseTermsConditionsLinkModule,
} from './components/delivery-defense-terms-conditions-link/delivery-defense-terms-conditions-link.module';
import { DisclaimerComponent } from './components/disclaimer/disclaimer.component';
import { DualRangeSliderModule } from './components/dual-range-slider/dual-range-slider.module';
import { ElementBlockerModule } from './components/element-blocker/element-blocker.module';
import { ErrorMessageComponent } from './components/error-message/error-message.component';
import { EventListModule } from './components/event-list/event-list.module';
import { FileSelectorModule } from './components/file-selector/file-selector.module';
import { GaugeComponent } from './components/gauge/gauge.component';
import { GridTitleComponent } from './components/grid-title/grid-title.component';
import { HeaderComponent } from './components/header/header.component';
import { ImprintComponent } from './components/imprint/imprint.component';
import {
    InternationalProductDisclosureComponent,
} from './components/international-product-disclosure/international-product-disclosure.component';
import { LanguageSelectorModule } from './components/language-selector/language-selector.module';
import { LanguageComponent } from './components/language/language.component';
import { DeliveryDefenseLogoComponent } from './components/logo-dd/logo-dd.component';
import { LogoComponent } from './components/logo/logo.component';
import { MenuModule } from './components/menu/menu.module';
import { CustomConfirmationComponent } from './components/modals/custom-confirmation/custom-confirmation.component';
import { CustomInfoComponent } from './components/modals/custom-info/custom-info.component';
import { NewsListModule } from './components/news-list/news-list.module';
import { NgxResponsiveTableModule } from './components/ngx-responsive-table/ngx-responsive-table.module';
import { NotificationsModule } from './components/notifications/notifications.module';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import {
    ParcelProTermsConditionsLinkModule,
} from './components/parcel-pro-terms-conditions-link/parcel-pro-terms-conditions-link.module';
import { PrivacyDialogComponent } from './components/privacy/privacy-dialog/privacy-dialog.component';
import { PrivacyComponent } from './components/privacy/privacy.component';
import { ResponsiveTableModule } from './components/responsive-table/responsive-table.module';
import { SearchModule } from './components/search/search.module';
import { ShipDetailsDialogModule } from './components/ship-details-dialog/ship-details-dialog.module';
import { ShipDetailsModule } from './components/ship-details/ship-details.module';
import { ShipLabelModule } from './components/ship-label/ship-label.module';
import { ShippingGuideDialogComponent } from './components/shipping-guide-dialog/shipping-guide-dialog.component';
import { SideBarMenuComponent } from './components/side-bar-menu/side-bar-menu.component';
import { SignalrifyComponent } from './components/signalrify/signalrify.component';
import { TermsOfUseDialogComponent } from './components/terms-of-use/terms-of-use-dialog/terms-of-use-dialog.component';
import { TermsOfUse } from './components/terms-of-use/terms-of-use.component';
import { TruncatedParagraphComponent } from './components/truncated-paragraph/truncated-paragraph.component';
import { UserBlockModule } from './components/user-block/user-block.module';
import { AppendCommaIfNotEmptyDirective } from './directives/append-comma-if-not-empty.directive';
import { BadgeDirective } from './directives/badge.directive';
import { RotaryCounterDirective } from './directives/rotary-counter.directive';
import { SpinOnDirective } from './directives/spin-on.directive';
import { UserInactivityDirective } from './directives/user-inactivity.directive';
import { Location } from './models/location/location.model';
import { MailAddress } from './models/mail-address.model';
import { TermsConditions } from './models/terms-conditions.model';
import { BypassSecurityPipe } from './pipes/bypass-security.pipe';
import { ConcatAddressPipeModule } from './pipes/concat-address/concat-address.module';
import { DayOfWeekPipeModule } from './pipes/day-of-week/day-of-week-pipe.module';
import { FirstErrorMessagePipe } from './pipes/first-error-message.pipe';
import { FormatTimePipeModule } from './pipes/format-time/format-time.module';
import { GoogleMapsUrlPipeModule } from './pipes/google-maps-url/google-maps-url.module';
import { ObjectKeysValuesPipeModule } from './pipes/object-keys-values/object-keys-values.module';
import { ReplacePipe } from './pipes/replace.pipe';
import { SafeStringModule } from './pipes/safe-string/safe-string.module';
import { ShipmentPipesModule } from './pipes/shipment/shipment-pipes.module';
import { ShippingStationPipe } from './pipes/shipping-station/shipping-station.pipe';
import { ToNumberPipe } from './pipes/to-number.pipe';
import { AnnouncementService } from './services/announcement/annoucement.service';
import { AuthGuardService } from './services/auth-guard/auth-guard.service';
import { BannerService } from './services/banner/banner.service';
import { BirService } from './services/bir/bir.service';
import { CarrierService } from './services/carrier/carrier.service';
import { ContactService } from './services/contact/contact.service';
import { CountryService } from './services/country/country.service';
import { DateTimeService } from './services/date-time/date-time.service';
import { DialogModule } from './services/dialog/dialog.module';
import { DialogService } from './services/dialog/dialog.service';
import { EmailService } from './services/email/email.service';
import { ErrorHandlerService } from './services/error-handler/error-handler.service';
import { BlobService } from './services/file/blob.service';
import { FileDownloadService } from './services/file/file-download.service';
import { FileService } from './services/file/file.service';
import { FormService } from './services/form/form.service';
import { GetIPService } from './services/get-ip/get-ip.service';
import { LookupService } from './services/lookup/lookup.service';
import { MailAddressService } from './services/mail-address/mail-address.service';
import { NotificationService } from './services/notification/notification.service';
import { RouteGuardService } from './services/route-guard/route-guard.service';
import { SingleSignOnService } from './services/single-sign-on/single-sign-on.service';
import { TranslationService } from './services/translation/translation.service';
import { User } from './services/user/models/user.model';
import { UserService } from './services/user/user.service';
import { UtilityService } from './services/utility/utility.service';
import { ValidationService } from './services/validation/validation.service';
import { ZipCodeService } from './services/zip-code/zip-code.service';


@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        AngularMaterialModule,
        TranslateModule,
        ElementBlockerModule,
        SearchModule,
        DataTableModule,
        DataTablesModule,
        NgxPaginationModule,
        ShipDetailsDialogModule,
        ShipDetailsModule,
        ShipLabelModule,
        ShipmentPipesModule,
        UserBlockModule,
        EventListModule,
        NewsListModule,
        ResponsiveTableModule,
        FileSelectorModule,
        MenuModule,
        SafeStringModule,
        LanguageSelectorModule,
        InlineSVGModule,
        NotificationsModule,
        DialogModule,
        NgxResponsiveTableModule,
        ConfidenceGaugeModule,
        DeliveryDefenseScoreSummaryModule,
        DualRangeSliderModule,
        ParcelProTermsConditionsLinkModule,
        DeliveryDefenseTermsConditionsLinkModule,
        DayOfWeekPipeModule,
        FormatTimePipeModule,
        ConcatAddressPipeModule,
        GoogleMapsUrlPipeModule,
        ObjectKeysValuesPipeModule,
        CarrierLogoModule,
        ContentLoadingProgressModule,
        NgOptimizedImage,
        BadgeDirective,
        BypassSecurityPipe,
    ],
    declarations: [
        LogoComponent,
        LanguageComponent,
        HeaderComponent,
        SideBarMenuComponent,
        CustomInfoComponent,
        CustomConfirmationComponent,
        ToNumberPipe,
        ErrorMessageComponent,
        GridTitleComponent,
        TruncatedParagraphComponent,
        PageHeaderComponent,
        PrivacyComponent,
        PrivacyDialogComponent,
        ShippingStationPipe,
        TermsOfUse,
        TermsOfUseDialogComponent,
        ShippingGuideDialogComponent,
        ComplaintsComponent,
        ImprintComponent,
        DisclaimerComponent,
        InternationalProductDisclosureComponent,
        UserInactivityDirective,
        DeliveryDefenseLogoComponent,
        GaugeComponent,
        AccessPointBannerComponent,
        AppendCommaIfNotEmptyDirective,
        ReplacePipe,
        SpinOnDirective,
        RotaryCounterDirective,
        AutoCloseDialogComponent,
        CircularConfidenceGaugeComponent,
        FirstErrorMessagePipe,
        SignalrifyComponent,
        CustomDatePickerComponent,
    ],
    providers: [
        AnnouncementService,
        AuthGuardService,
        BannerService,
        BirService,
        BlobService,
        CarrierService,
        ContactService,
        CountryService,
        DateTimeService,
        DialogService,
        EmailService,
        ErrorHandlerService,
        FileDownloadService,
        FileService,
        FormService,
        GetIPService,
        Location,
        LookupService,
        MailAddress,
        MailAddressService,
        NotificationService,
        RouteGuardService,
        ShippingStationPipe,
        TermsConditions,
        TranslationService,
        User,
        UserService,
        UtilityService,
        ValidationService,
        ZipCodeService,
        SingleSignOnService,
        UserInactivityDirective,
        CookieService,
    ],
    exports: [
        CommonModule,
        RouterModule,
        AngularMaterialModule,
        ElementBlockerModule,
        LogoComponent,
        LanguageComponent,
        HeaderComponent,
        SideBarMenuComponent,
        CustomInfoComponent,
        CustomConfirmationComponent,
        ToNumberPipe,
        ErrorMessageComponent,
        GridTitleComponent,
        ShipDetailsModule,
        ShipLabelModule,
        ShipmentPipesModule,
        UserBlockModule,
        EventListModule,
        NewsListModule,
        TranslateModule,
        ResponsiveTableModule,
        FileSelectorModule,
        MenuModule,
        SafeStringModule,
        LanguageSelectorModule,
        TruncatedParagraphComponent,
        DataTablesModule,
        NgxPaginationModule,
        PageHeaderComponent,
        PrivacyComponent,
        PrivacyDialogComponent,
        InlineSVGModule,
        DialogModule,
        NgxResponsiveTableModule,
        ComplaintsComponent,
        DisclaimerComponent,
        InternationalProductDisclosureComponent,
        UserInactivityDirective,
        DeliveryDefenseLogoComponent,
        GaugeComponent,
        AccessPointBannerComponent,
        AppendCommaIfNotEmptyDirective,
        ReplacePipe,
        SpinOnDirective,
        RotaryCounterDirective,
        AutoCloseDialogComponent,
        CircularConfidenceGaugeComponent,
        ConfidenceGaugeModule,
        DeliveryDefenseScoreSummaryModule,
        DualRangeSliderModule,
        FirstErrorMessagePipe,
        DayOfWeekPipeModule,
        FormatTimePipeModule,
        ConcatAddressPipeModule,
        GoogleMapsUrlPipeModule,
        ObjectKeysValuesPipeModule,
        CarrierLogoModule,
        SignalrifyComponent,
        CustomDatePickerComponent,
    ],
})
export class SharedModule {
}
